/**
 * Phase 12.3 — Print size slider (safe-area scale → new version, no AI quota).
 */

/**
 * @param {HTMLElement} root
 * @param {{
 *   getSelectedDesign: () => Record<string, unknown>|null,
 *   getToken: () => string,
 *   getActiveSide: () => 'front'|'back',
 *   onApplied: (payload: Record<string, unknown>) => Promise<void>|void,
 *   withBusy: (fn: () => Promise<void>) => Promise<void>,
 *   scalePrint: (token: string, body: { side: string, print_scale: number, design_id?: number|null }) => Promise<Record<string, unknown>>,
 * }} deps
 */
export function bindPrintSizePanel(root, deps) {
    const panel = root.querySelector('#bag-print-size-panel');
    if (!panel || panel.getAttribute('data-print-scale-enabled') !== '1') {
        return {
            refresh() {},
            setStatus() {},
        };
    }

    const range = /** @type {HTMLInputElement|null} */ (panel.querySelector('#bag-print-size-range'));
    const valueEl = panel.querySelector('#bag-print-size-value');
    const applyBtn = /** @type {HTMLButtonElement|null} */ (panel.querySelector('#bag-print-size-apply'));
    const statusEl = panel.querySelector('#bag-print-size-status');

    const minScale = clampScale(parseFloat(panel.getAttribute('data-print-scale-min') || '0.55'), 0.2, 1);
    const maxScale = clampScale(parseFloat(panel.getAttribute('data-print-scale-max') || '1'), minScale, 1);

    if (range) {
        range.min = String(Math.round(minScale * 100));
        range.max = String(Math.round(maxScale * 100));
        range.value = String(Math.round(maxScale * 100));
    }

    function setStatus(message, isError) {
        if (!statusEl) {
            return;
        }
        statusEl.textContent = message || '';
        statusEl.classList.toggle('text-danger', Boolean(isError));
        statusEl.classList.toggle('text-muted', !isError);
    }

    function syncLabel() {
        if (!range) {
            return;
        }
        const pct = Number(range.value) || Math.round(maxScale * 100);
        if (valueEl) {
            valueEl.textContent = pct + '%';
        }
        range.setAttribute('aria-valuetext', pct + ' percent');
    }

    function refresh() {
        const design = deps.getSelectedDesign();
        const ok = Boolean(
            design
            && design.generation_status === 'ok'
            && design.id,
        );
        panel.hidden = !ok;
        if (!ok || !range) {
            setStatus('');
            return;
        }

        const current = typeof design.print_scale === 'number' && design.print_scale > 0
            ? design.print_scale
            : maxScale;
        const pct = Math.round(clampScale(current, minScale, maxScale) * 100);
        range.value = String(pct);
        syncLabel();
        setStatus('');
    }

    if (range) {
        range.addEventListener('input', () => {
            syncLabel();
        });
    }

    if (applyBtn) {
        applyBtn.addEventListener('click', () => {
            const design = deps.getSelectedDesign();
            if (!design || !design.id) {
                setStatus('Select a finished design first.', true);
                return;
            }
            const token = deps.getToken();
            if (!token) {
                setStatus('Design session not ready.', true);
                return;
            }
            const pct = range ? Number(range.value) : Math.round(maxScale * 100);
            const scale = clampScale(pct / 100, minScale, maxScale);

            deps.withBusy(async () => {
                setStatus('Applying print size…', false);
                const result = await deps.scalePrint(token, {
                    side: deps.getActiveSide(),
                    print_scale: scale,
                    design_id: Number(design.id),
                });
                await deps.onApplied(result);
                setStatus('New version created at ' + Math.round(scale * 100) + '%.', false);
            }, {
                title: 'Updating print size…',
                detail: '',
            }).catch((err) => {
                setStatus(err && err.message ? err.message : 'Could not apply print size.', true);
            });
        });
    }

    syncLabel();
    refresh();

    return { refresh, setStatus };
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clampScale(value, min, max) {
    if (!Number.isFinite(value)) {
        return max;
    }
    return Math.max(min, Math.min(max, value));
}
